import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Accordion from "react-bootstrap/Accordion";
import Heading from "../../../shared_components/Heading/Heading";
import UserData from "../../../services/UserData";
import AIPreEnrichmentService from "../../../services/AIPreEnrichmentService";
import { DetailFields, label } from "../PreEnrichmentRuns/DetailFields";
import layout from "../IngestionBatches/IngestionBatches.module.css";
import shared from "../PreEnrichmentRuns/PreEnrichmentRuns.module.css";
import style from "./PreEnrichmentRulesets.module.css";

const Enabled = ({ value }) => <span className={style.activePill} data-active={value}>{value === true ? "Enabled" : value === false ? "Disabled" : "Not specified"}</span>;

const RulesetRules = () => {
  const { rulesetId } = useParams();
  const userData = useContext(UserData);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setData(null);
    setLoading(true);
    setError(false);
    if (!userData) return () => { active = false; };
    AIPreEnrichmentService.getRulesetRules(userData, rulesetId)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userData, rulesetId, retry]);

  return (
    <section className={layout.container} aria-label="Rules and Derivations" aria-busy={loading}>
      <div>
        <Link className="btn btn-outline-secondary btn-sm mb-3" to="/intelligence/pre-enrichment-rulesets"><ArrowLeft size={18} aria-hidden="true" /> Back to Pre-Enrichment Rulesets</Link>
        <Heading>Rules and Derivations</Heading>
        {data && <div className={shared.runId}>{data.ruleset_id} · {data.version}</div>}
      </div>
      <div className={layout.loadingSlot}>{loading && <div className={layout.loadingBar} role="progressbar" aria-label="Loading rules and derivations" />}</div>
      <div className={`${layout.content} ${shared.documentDetails}`}>
        {error && <div role="alert">Unable to load rules and derivations. <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRetry(value => value + 1)}>Retry</button></div>}
        {data && <>
          <p>{data.evaluation_explanation}</p>
          <p>Total rules: {data.total_rules ?? data.rules.length} · Enabled: {data.enabled_rules ?? "—"} · Disabled: {data.disabled_rules ?? "—"} · Returned: {data.returned_rules ?? data.rules.length}</p>
          <Accordion defaultActiveKey={["rules"]} alwaysOpen>
          <Accordion.Item eventKey="rules">
            <Accordion.Header>Rules ({data.rules.length})</Accordion.Header>
            <Accordion.Body>
            {data.rules.length === 0 && <p>No rules found for this ruleset.</p>}
            {data.rules.map(rule => <article className={style.card} key={rule.id || rule.rule_id}>
              <div className={style.cardHeading}>
                <h4>{rule.name || rule.rule_id}</h4>
                <Enabled value={rule.enabled} />
              </div>
              <p>{rule.explanation || rule.reason}</p>
              <DetailFields
                data={Object.fromEntries(Object.entries(rule).filter(([key]) => !["name", "enabled", "decision", "explanation"].includes(key)))}
                renderField={(key, value) => key === "action" && value && typeof value === "object" ? (
                  <DetailFields data={value} renderField={(actionKey, actionValue) => actionKey === "decision" && typeof actionValue === "string" ? (
                    <span className={shared.decisionPill} data-decision={actionValue}>{label(actionValue.toLowerCase())}</span>
                  ) : undefined} />
                ) : undefined}
              />
            </article>)}
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="derivations">
            <Accordion.Header>Derivations ({data.derivations.length})</Accordion.Header>
            <Accordion.Body>
            {data.derivations.length === 0 && <p>No derivations found for this ruleset.</p>}
            {data.derivations.map(derivation => <article className={style.card} key={derivation.id}>
              <div className={style.cardHeading}><h4>{derivation.type ? label(derivation.type.toLowerCase()) : derivation.id}</h4><Enabled value={derivation.enabled} /></div>
              <DetailFields data={Object.fromEntries(Object.entries(derivation).filter(([key]) => key !== "enabled"))} />
            </article>)}
            </Accordion.Body>
          </Accordion.Item>
          </Accordion>
        </>}
      </div>
    </section>
  );
};

export default RulesetRules;
